import { cloneElement, createContext, ReactElement, useContext, useMemo } from 'react';
import { Alert as ChakraAlert, AlertProps, forwardRef } from '@chakra-ui/react';

import { ColorScheme } from '../../types/bitkit';
import Box from '../Box/Box';
import CloseButton from '../CloseButton/CloseButton';
import ColorButton, { ColorButtonProps } from '../ColorButton/ColorButton';
import Icon, { TypeIconName } from '../Icon/Icon';
import ProgressSpinner from '../ProgressSpinner/ProgressSpinner';

type NotificationStatus = 'info' | 'error' | 'success' | 'warning' | 'progress';

export const NOTIFICATION_STATUSES: Record<
  NotificationStatus,
  { colorScheme: ColorScheme; defaultIcon: ReactElement }
> = {
  error: { colorScheme: 'red', defaultIcon: <Icon name="ErrorCircleFilled" /> },
  info: { colorScheme: 'blue', defaultIcon: <Icon name="InfoCircle" /> },
  progress: {
    colorScheme: 'purple',
    defaultIcon: (
      <Box height={24} padding={2} width={24}>
        <ProgressSpinner size={20} />
      </Box>
    ),
  },
  success: { colorScheme: 'green', defaultIcon: <Icon name="Check" /> },
  warning: { colorScheme: 'yellow', defaultIcon: <Icon name="Warning" /> },
} as const;

export type ActionProps = {
  label: string;
  href?: string;
  target?: string;
  onClick?: () => void;
  placement?: 'end' | 'after-content';
} & Omit<ColorButtonProps, 'as' | 'onClick' | 'colorScheme'>;
export interface NotificationProps extends Omit<AlertProps, 'status' | 'colorScheme' | 'onClose'> {
  status: NotificationStatus;
  onClose?: () => void;
  action?: ActionProps;
  iconName?: TypeIconName;
  showIcon?: boolean;
}

const ActionContext = createContext<{ colorScheme: ColorScheme }>({
  colorScheme: 'blue',
});

const NotificationAction = ({ href, label, ...rest }: ActionProps) => {
  const actionContext = useContext(ActionContext);
  return (
    <ColorButton
      as={(href ? 'a' : 'button') as any}
      flexGrow={0}
      flexShrink={0}
      href={href}
      {...actionContext}
      {...rest}
    >
      {label}
    </ColorButton>
  );
};

const Notification = forwardRef<NotificationProps, 'div'>((props, ref) => {
  const { action, children, iconName, onClose, showIcon = true, status, ...rest } = props;
  const { colorScheme, defaultIcon } = NOTIFICATION_STATUSES[status];
  const icon = iconName ? <Icon name={iconName} /> : defaultIcon;
  let actionButton;
  const actionPlacement = action?.placement ?? 'end';
  if (action) {
    actionButton = <NotificationAction marginRight="12" {...action} alignSelf={['start', 'center']} />;
  }
  const actionContext = useMemo(() => ({ colorScheme }), [colorScheme]);
  // This is needed when the actionButton is rendered with a one-liner text.
  // Without this both the icon and the text would be strangely aligned.
  const marginTop = actionButton ? 4 : undefined;
  return (
    <ChakraAlert ref={ref} {...rest} colorScheme={colorScheme}>
      <ActionContext.Provider value={actionContext}>
        {showIcon && cloneElement(icon, { flexShrink: 0, marginTop })}
        <Box display="flex" flexDirection={['column', 'row']} flexGrow={1} gap={16} justifyContent="start">
          <Box flexGrow={actionPlacement === 'end' ? 1 : undefined} marginTop={marginTop} wordBreak="break-word">
            {children}
          </Box>
          {actionButton}
        </Box>
        {onClose && (
          <CloseButton
            colorScheme={colorScheme}
            flexShrink={0}
            marginLeft="auto"
            marginTop={marginTop}
            onClick={onClose}
          />
        )}
      </ActionContext.Provider>
    </ChakraAlert>
  );
});

export { NotificationAction };
export default Notification;
